import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router';
import { Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { toast } from 'sonner';
import Logo from '../components/Logo';
import { makeRESTApiRequest } from '../helpers/api';
import { ENDPOINTS } from '../helpers/constants';
import { saveUserToLocal } from '../helpers/session';
import { isAdmin, isClient, isOrgAdmin, getUserFromLocalstrorage, isSalesPerson } from '../helpers/global';
import { ArrowRight, Eye, EyeOff, Key, KeyRound, Mail } from 'lucide-react';
import SetPasswordGoogleButton from '../components/SetPasswordGoogleButton';
import InvalidInviteLinkAlert from '../components/InvalidInviteLinkAlert';
import CircularProgressBar from '../components/circular-progress';

const useQuery = () => {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
};

const SetPassword = () => {
  const query = useQuery();
  const history = useHistory();

  const isHandlingGoogleCallback = useRef(false);
  const [token, setToken] = useState(query.get('token') || '');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isInvalidLink, setIsInvalidLink] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const getEmailFromTemporaryKey = async () => {
    if (!token) return;
    try {
      setIsLoading(true);
      const { response: emailResult, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.GET_EMAIL_FROM_TEMPORARY_KEY,
        requestBody: { temporaryKey: token }
      });
      if (error || !emailResult.isValid) {
        toast.error(error);
        setIsInvalidLink(true);
        return;
      }
      setEmail(emailResult.email);
      setToken(token);
    } catch (error) {
      console.error(error);
      setIsInvalidLink(true);
      return;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    // Skip temp key validation during Google OAuth callback
    if (code && state) return;

    getEmailFromTemporaryKey();
  }, [token]);

  // Redirect if user is already logged in
  useEffect(() => {
    const user = getUserFromLocalstrorage();
    const userToken = localStorage.getItem('user-token');

    if (user && userToken) {
      history.push(isClient(user?.role) ? '/org-dashboard' : isAdmin(user?.role) || isSalesPerson(user?.role) ? '/dashboard' : '/services/pipeline');
    }
  }, [history]);

  useEffect(() => {
    // Don't redirect if we're handling a Google OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    // If code and state are present, we're in the middle of Google OAuth, don't redirect
    if (code && state) {
      return;
    }

    // Only redirect if no email/token AND not in OAuth callback
    if (!token && !email) {
      history.push('/');
    }
  }, [email, token, history]);

  // Handle Google OAuth callback
  useEffect(() => {
    const handleGoogleCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');

      if (code && state) {
        if (isHandlingGoogleCallback.current) return;
        isHandlingGoogleCallback.current = true;
        try {
          let stateData;
          try {
            stateData = JSON.parse(decodeURIComponent(state));
          } catch (e) {
            toast.error('Invalid authentication state. Please try again.');
            return;
          }

          // Get email and token from stateData (OAuth callback) or URL params (direct access)
          const oauthEmail = stateData?.email || email;
          const oauthToken = stateData?.token || token;

          setEmail(oauthEmail);
          setToken(oauthToken);

          if (!oauthEmail || !oauthToken) {
            toast.error('Invalid authentication state. Please try again.');
            return;
          }

          // Use fixed redirect URI without query parameters
          const redirectUri = `${window.location.origin}/set-password`;

          setSubmitting(true);

          // Call backend to register with Google (backend will exchange code for tokens)
          const { response: authResult, error } = await makeRESTApiRequest({
            endpoint: ENDPOINTS.REGISTER_WITH_GOOGLE,
            requestBody: { email: oauthEmail, temporaryKey: oauthToken, code, redirectUri }
          });

          setSubmitting(false);

          if (error) {
            toast.error(error);
            // Clean URL and redirect back with email/token for manual password entry
            history.replace(`/set-password?token=${encodeURIComponent(oauthToken)}`);
            return;
          }

          saveUserToLocal(authResult, true);
          const role = authResult.role;
          if (isOrgAdmin(role)) {
            history.push('/org-dashboard?onboarding=true');
          } else if (role === 'Org Staff') {
            history.push('/');
          } else {
            history.push(isClient(role) ? '/org-dashboard' : isAdmin(role) || isSalesPerson(role) ? '/dashboard' : '/services/pipeline');
          }
        } catch (err) {
          setSubmitting(false);
          toast.error('Google authentication failed. Please try again.');
          console.error(err);
          // Clean URL and redirect back with email/token for manual password entry
          const oauthEmail = email;
          const oauthToken = token;
          history.replace(`/set-password?token=${encodeURIComponent(oauthToken)}`);
        }
      }
    };

    handleGoogleCallback();
  }, [email, token, history]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!email || !token) {
      toast.error('Invalid or missing invitation link.');
      return;
    }
    if (!password || !confirmPassword) {
      toast.error('Please enter and confirm your password.');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const { response: authResult, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.REGISTER,
        requestBody: { email, password, temporaryKey: token }
      });
      setSubmitting(false);
      if (error) {
        toast.error(error);
        return;
      }
      saveUserToLocal(authResult, true);
      const role = authResult.role;
      // Redirect to appropriate dashboard - add onboarding query for Org Admin
      if (isOrgAdmin(role)) {
        history.push('/org-dashboard?onboarding=true');
      } else {
        history.push(isClient(role) ? '/org-dashboard' : isAdmin(role) || isSalesPerson(role) ? '/dashboard' : '/services/pipeline');
      }
    } catch (err) {
      setSubmitting(false);
      toast.error('Something went wrong! Please try again.');
      console.error(err);
    }
  };

  const PasswordIcon = ({ showPassword, onClick }) => {
    const Icon = showPassword ? EyeOff : Eye;
    return (
      <Icon
        size={16}
        className=" text-muted cursor-pointer"
        style={{ position: 'absolute', top: '50%', right: '10px', transform: 'translateY(-50%)' }}
        onClick={onClick}
      />
    );
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <CircularProgressBar size={10} />
      </div>
    );
  }

  if (isInvalidLink) {
    return <InvalidInviteLinkAlert />;
  }

  return (
    <Container fluid className="h-100">
      <Row className="h-100">
        <Col xs={1} className="bg-dark d-block"></Col>
        <Col xs={11} className="bg-light h-100 d-flex flex-column p-0 overflow-auto">
          <Row className="justify-content-center flex-grow-1 py-2 px-0 px-md-5">
            <Col xs={10} md={8} lg={6} xl={5} className="align-self-center">
              <Card className="p-2 border-0 bg-transparent">
                <Card.Header className="p-0 mt-3 mb-2 bg-transparent border-0 text-center">
                  <div className="text-center">
                    <Logo />
                  </div>
                  <h6 className="text-muted mx-2  mt-4">
                    <b className="text-dark font-weight-medium xlargeFont">
                      Hello! Set your password to login to your account.
                    </b>
                  </h6>
                </Card.Header>
                <Card.Body>
                  <form onSubmit={handleSubmit} className="mx-2">
                    <div className="form-group mb-3">
                      {/* <label className="midFont mb-1">Email</label> */}

                      <div className="position-relative">
                        <Form.Control
                          type="text"
                          className="bg-transparent"
                          placeholder="Your email"
                          style={{ paddingLeft: '35px', height: '44px' }}
                          value={email}
                          readOnly
                        />

                        <Mail
                          size={20}
                          className=" text-muted"
                          style={{ position: 'absolute', top: '50%', left: '10px', transform: 'translateY(-50%)' }}
                        />
                      </div>
                    </div>

                    <div className="form-group mt-2">
                      {/* <label className="midFont mb-1">New Password</label> */}
                      <div className="position-relative">
                        <Form.Control
                          className="bg-transparent"
                          placeholder="Enter your password"
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          autoFocus
                          required
                          style={{ padding: '0 35px', height: '44px' }}
                        />
                        <KeyRound
                          size={20}
                          className=" text-muted"
                          style={{ position: 'absolute', top: '50%', left: '10px', transform: 'translateY(-50%)' }}
                        />
                        <PasswordIcon showPassword={showPassword} onClick={() => setShowPassword(!showPassword)} />
                      </div>
                    </div>
                    <div className="form-group mt-2">
                      {/* <label className="midFont mb-1">Confirm Password</label> */}
                      <div className="position-relative">
                        <Form.Control
                          className="bg-transparent"
                          placeholder="Confirm your password"
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={e => setConfirmPassword(e.target.value)}
                          required
                          style={{ padding: '0 35px', height: '44px' }}
                        />
                        <KeyRound
                          size={20}
                          className=" text-muted"
                          style={{ position: 'absolute', top: '50%', left: '10px', transform: 'translateY(-50%)' }}
                        />
                        <PasswordIcon
                          showPassword={showConfirmPassword}
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="btn btn-primary font-weight-bold mt-1 w-100"
                      style={{ backgroundColor: '#facc15' }}
                    >
                      <div className="d-flex align-items-center justify-content-center">
                        Submit{' '}
                        {submitting ? (
                          <Spinner size="sm" animation="border" className="ml-1" />
                        ) : (
                          <ArrowRight size={16} className="ml-1" />
                        )}
                      </div>
                    </button>
                    <div className="text-center mt-3">
                      <span className="text-muted small">OR</span>
                    </div>
                    <div className="mt-3">
                      <SetPasswordGoogleButton email={email} token={token} className="mb-2" />
                    </div>
                  </form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  );
};

export default SetPassword;
